/** "Don't show on save for this module": the save warnings' mute.
 *
 *  Kept in this browser, per module (its scope, `al_meu` or the project id,
 *  and its code), as the set of warnings the architect saw when muting. A save
 *  is silent while every warning it returns is in that set; a warning not in
 *  it re-arms the toast. The badge in the header is never muted. Storage can
 *  be missing or throw (a private window, blocked site data): then nothing is
 *  muted, and `setMuted` says it could not keep the choice. */
import type { Avertisment } from "./avertismente";

const KEY = "ob.editor.avertismente_tacute";

/** What tells one warning from another: its code, its apartment and every
 *  name it carries (a wall, a variant, a room, a core part). Measured numbers
 *  (areas, thresholds, coordinates) are left out, so a muted warning stays
 *  muted while its figure moves. */
export function warningSignature(a: Avertisment): string {
  const date = a.date ?? {};
  const names = Object.keys(date).sort()
    .filter((k) => typeof date[k] === "string")
    .map((k) => `${k}=${date[k] as string}`);
  return [a.cod, `locuinta=${date.locuinta ?? ""}`, ...names].join("|");
}

const key = (scope: string, code: string) => `${KEY}:${scope}:${code}`;

function stored(scope: string, code: string): Set<string> {
  try {
    const raw = localStorage.getItem(key(scope, code));
    const list = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(list) ? list.map(String) : []);
  } catch {
    return new Set();
  }
}

export function isMuted(scope: string, code: string, list: readonly Avertisment[]): boolean {
  if (!list.length) return false;
  const muted = stored(scope, code);
  return list.every((a) => muted.has(warningSignature(a)));
}

/** Mutes the warnings of `list` for this module, or clears the mute (`null`).
 *  Returns false when the browser would not keep it. */
export function setMuted(scope: string, code: string, list: readonly Avertisment[] | null): boolean {
  try {
    if (list === null) localStorage.removeItem(key(scope, code));
    else localStorage.setItem(key(scope, code), JSON.stringify(list.map(warningSignature)));
    return true;
  } catch {
    return false;
  }
}

/** Whether a save that returned `list` gets the warnings toast. */
export function shouldAnnounce(scope: string, code: string, list: readonly Avertisment[]): boolean {
  return list.length > 0 && !isMuted(scope, code, list);
}
